import { useCallback } from 'react';
import { TradeState } from '../types/trade';
import { BidStatus, BidStatusName } from '../types/auction';

const usePopulateBidContext = ({
  auctionSummary,
  bidSource,
  isAuctionEnded,
  isMultiOrgAndEnabled,
  setBidContext,
  tradeState,
  tradeSummary,
  upcomingProxy,
  user
}) => {
  return useCallback(() => {
    if (!setBidContext) {
      return;
    }

    const isCsu = user?.superUserId && user?.superUserId !== user?.id;

    let modalVariant;
    if (tradeState === TradeState.active) {
      modalVariant = BidStatus.none;

      if (
        auctionSummary?.userBidStatus === BidStatus.topBidder ||
        auctionSummary?.userBidStatus === BidStatus.proxy
      ) {
        modalVariant = BidStatus.topBidder;
      } else if (auctionSummary?.userBidStatus === BidStatus.outbid) {
        modalVariant = BidStatus.outbid;
      }
    }

    let proxyAmount;

    if (tradeState === TradeState.active) {
      proxyAmount =
        auctionSummary?.proxyAmount > auctionSummary?.maxBid
          ? auctionSummary?.proxyAmount
          : undefined;
    } else {
      proxyAmount = upcomingProxy?.proxyAmount;
    }

    const userDealerGroupIds =
      isMultiOrgAndEnabled && user?.multiOrgDealerGroupIds?.length
        ? user?.multiOrgDealerGroupIds
        : user?.dealerGroupIds;
    const isOutbidDealer =
      auctionSummary?.topBidderSummary?.companyId &&
      auctionSummary?.topBidderSummary?.companyId === user?.companyId &&
      user?.id !== auctionSummary?.topBidderSummary?.id;
    const isSameDGN = auctionSummary?.topBidderSummary?.dealerGroupIds?.some?.(
      id => userDealerGroupIds?.indexOf(id) !== -1
    );
    const isOutbidDGN =
      auctionSummary?.topBidderSummary?.dealerGroupIds?.length &&
      (isSameDGN || auctionSummary?.topBidderSummary?.isSameDealerGroup) &&
      auctionSummary?.topBidderSummary?.id !== user?.id;

    const tradeContext = {
      ...tradeSummary,
      presetBidAmount: upcomingProxy?.bidAmount,
      currentBidAmount:
        tradeState === TradeState.active ? auctionSummary?.maxBid : undefined,
      currentProxyAmount: proxyAmount,
      upcomingProxy: TradeState.upcoming ? upcomingProxy : undefined,
      currentUpcomingProxy: TradeState.upcoming ? upcomingProxy : undefined,
      proxyAmount:
        tradeState === TradeState.active
          ? auctionSummary?.proxyAmount
          : upcomingProxy?.proxyAmount,
      numberOfBids: auctionSummary?.numBids,
      isAuctionEnded,
      isOutBidSameDealer: isOutbidDealer,
      isOutBidSameDGN: isOutbidDGN,
      bidStatus:
        tradeState === TradeState.upcoming
          ? BidStatus.proxy
          : auctionSummary?.userBidStatus,
      isEditing: true,
      isCsu,
      isSettingProxy:
        (tradeState === TradeState.active &&
          auctionSummary?.proxyAmount > auctionSummary?.maxBid) ||
        (tradeState === TradeState.upcoming &&
          upcomingProxy?.proxyAmount != null),
      variant: BidStatusName[modalVariant],
      bidSource
    };

    setBidContext(context => ({
      ...context,
      ...tradeContext
    }));
  }, [
    setBidContext,
    user?.superUserId,
    user?.id,
    user?.multiOrgDealerGroupIds,
    user?.dealerGroupIds,
    user?.companyId,
    tradeState,
    isMultiOrgAndEnabled,
    auctionSummary?.topBidderSummary?.companyId,
    auctionSummary?.topBidderSummary?.id,
    auctionSummary?.topBidderSummary?.dealerGroupIds,
    auctionSummary?.topBidderSummary?.isSameDealerGroup,
    auctionSummary?.maxBid,
    auctionSummary?.proxyAmount,
    auctionSummary?.numBids,
    auctionSummary?.userBidStatus,
    tradeSummary,
    upcomingProxy,
    isAuctionEnded,
    bidSource
  ]);
};

export default usePopulateBidContext;
